import { useState, useCallback } from 'react';
import { saveSubmission } from '../firebase/submissions';
import '../styles/setup-form.css';

/* ── icon helpers ── */
const ArrowRight = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);
const ArrowLeft = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);

/* ══════════════════════════════════════════════════
   REUSABLE FIELD COMPONENTS
══════════════════════════════════════════════════ */
function Field({ label, required, hint, children, error }) {
  return (
    <div className="sf-field">
      {label && (
        <label className="sf-label">
          {label}
          {required && <span className="sf-req" aria-hidden="true"> *</span>}
        </label>
      )}
      {children}
      {hint && <p className="sf-hint">{hint}</p>}
      {error && <p className="sf-err">{error}</p>}
    </div>
  );
}

function Input({ id, type = 'text', placeholder, value, onChange, hasError }) {
  return (
    <input
      id={id}
      type={type}
      className={`sf-input ${hasError ? 'sf-input--err' : ''}`}
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      autoComplete="off"
    />
  );
}

function Select({ id, value, onChange, children, hasError }) {
  return (
    <select
      id={id}
      className={`sf-input sf-select ${hasError ? 'sf-input--err' : ''}`}
      value={value}
      onChange={e => onChange(e.target.value)}
    >
      {children}
    </select>
  );
}

function Textarea({ id, placeholder, value, onChange, minH = 88, hasError }) {
  return (
    <textarea
      id={id}
      className={`sf-input sf-textarea ${hasError ? 'sf-input--err' : ''}`}
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{ minHeight: minH }}
    />
  );
}

function CheckGrid({ options, values, onChange }) {
  const toggle = (val) => {
    const next = values.includes(val)
      ? values.filter(v => v !== val)
      : [...values, val];
    onChange(next);
  };
  return (
    <div className="sf-check-grid">
      {options.map(({ value, label, sub }) => (
        <label
          key={value}
          className={`sf-check-item ${values.includes(value) ? 'sf-check-item--on' : ''}`}
        >
          <input
            type="checkbox"
            checked={values.includes(value)}
            onChange={() => toggle(value)}
            className="sf-check-input"
          />
          <div>
            <div className="sf-ci-label">{label}</div>
            {sub && <div className="sf-ci-sub">{sub}</div>}
          </div>
        </label>
      ))}
    </div>
  );
}

function RadioGroup({ name, options, value, onChange }) {
  return (
    <div className="sf-radio-group">
      {options.map(opt => (
        <label
          key={opt.value}
          className={`sf-radio-item ${value === opt.value ? 'sf-radio-item--on' : ''}`}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="sf-radio-input"
          />
          <div>
            <div className="sf-ri-label">{opt.label}</div>
            {opt.sub && <div className="sf-ri-sub">{opt.sub}</div>}
          </div>
        </label>
      ))}
    </div>
  );
}

function TagGroup({ options, values, onChange }) {
  const toggle = (val) => {
    const next = values.includes(val)
      ? values.filter(v => v !== val)
      : [...values, val];
    onChange(next);
  };
  return (
    <div className="sf-tag-wrap">
      {options.map(val => (
        <button
          key={val}
          type="button"
          onClick={() => toggle(val)}
          className={`sf-tag ${values.includes(val) ? 'sf-tag--on' : ''}`}
        >
          {val}
        </button>
      ))}
    </div>
  );
}

function GroupLabel({ children }) {
  return <div className="sf-group-label">{children}</div>;
}

/* ══════════════════════════════════════════════════
   INITIAL STATE
══════════════════════════════════════════════════ */
const INIT = {
  /* step 1 – business */
  bizname: '', industry: '', website: '', city: '', country: '', phone: '', bizdes: '',
  /* step 2 – branding */
  agentname: '', voice: '', tone: '', firstmsg: '', color: '', domain: '', logo: '',
  /* step 3 – channels */
  channels: [], booking: '', cal_other: '', cal_email: '', hours: '', appt_dur: '',
  /* step 4 – knowledge */
  services: '', faqs: '', never: '', docs: '', languages: [], lang_other: '',
  /* step 5 – integrations */
  crm: [], payments: [], ecom: [], other_tools: '', notify: '',
  /* step 6 – contact */
  name: '', role: '', email: '', mobile: '', availability: '', plan: '', source: '', notes: '',
};

/* ══════════════════════════════════════════════════
   STEP DEFINITIONS
══════════════════════════════════════════════════ */
const STEP_META = [
  { short: 'Business',     label: 'Step 1 of 6 — Business basics' },
  { short: 'Branding',     label: 'Step 2 of 6 — AI agent branding' },
  { short: 'Channels',     label: 'Step 3 of 6 — Channels & booking' },
  { short: 'Knowledge',    label: 'Step 4 of 6 — Knowledge base' },
  { short: 'Integrations', label: 'Step 5 of 6 — Tools & integrations' },
  { short: 'Your details', label: 'Step 6 of 6 — Your contact info' },
];

const REQUIRED = [
  ['bizname', 'industry', 'city', 'country', 'bizdes'],
  ['agentname', 'firstmsg'],
  [],
  ['services'],
  [],
  ['name', 'role', 'email', 'mobile'],
];

/* ══════════════════════════════════════════════════
   STEP PANELS
══════════════════════════════════════════════════ */
function Step1({ d, set }) {
  const s = (k) => (v) => set(k, v);
  return (
    <>
      <div className="sf-row-2">
        <Field label="Business name" required>
          <Input id="bizname" placeholder="e.g. Smith Dental Clinic" value={d.bizname} onChange={s('bizname')} />
        </Field>
        <Field label="Industry" required>
          <Select id="industry" value={d.industry} onChange={s('industry')}>
            <option value="">Select industry…</option>
            {['Healthcare / Clinic','Real Estate','Legal / Law Firm','Trades / Home Services',
              'Accounting / Finance','E-Commerce / Retail','Hospitality / Restaurant',
              'Fitness / Wellness','Education / Tutoring','Other'].map(o => (
              <option key={o}>{o}</option>
            ))}
          </Select>
        </Field>
      </div>
      <Field label="Business website URL" hint="We'll auto-scrape this to build your AI's knowledge base.">
        <Input id="website" type="url" placeholder="https://yourbusiness.com" value={d.website} onChange={s('website')} />
      </Field>
      <div className="sf-row-2">
        <Field label="City / Region" required>
          <Input id="city" placeholder="e.g. Toronto, ON" value={d.city} onChange={s('city')} />
        </Field>
        <Field label="Country" required>
          <Select id="country" value={d.country} onChange={s('country')}>
            <option value="">Select…</option>
            {['Canada','United States','United Kingdom','Australia','Other'].map(o => (
              <option key={o}>{o}</option>
            ))}
          </Select>
        </Field>
      </div>
      <Field label="Business phone number" hint="Your existing number — we'll route calls through your AI agent.">
        <Input id="phone" type="tel" placeholder="+1 (416) 000-0000" value={d.phone} onChange={s('phone')} />
      </Field>
      <Field label="What does your business do?" required
        hint="Be as specific as possible — this goes directly into your AI's training.">
        <Textarea id="bizdes" value={d.bizdes} onChange={s('bizdes')}
          placeholder="e.g. We're a dental clinic in Toronto offering cleanings, fillings, Invisalign, and emergency appointments. We serve families and accept most major insurance plans." minH={110} />
      </Field>
    </>
  );
}

function Step2({ d, set }) {
  const s = (k) => (v) => set(k, v);
  return (
    <>
      <div className="sf-row-2">
        <Field label="AI agent name" required hint="A name that fits your brand personality.">
          <Input id="agentname" placeholder="e.g. Sophie, Alex, Maya" value={d.agentname} onChange={s('agentname')} />
        </Field>
        <Field label="Agent voice gender">
          <Select id="voice" value={d.voice} onChange={s('voice')}>
            <option value="">No preference</option>
            {['Female','Male','Neutral'].map(o => <option key={o}>{o}</option>)}
          </Select>
        </Field>
      </div>
      <Field label="Tone & personality" required>
        <RadioGroup name="tone" value={d.tone} onChange={s('tone')} options={[
          { value: 'Professional & formal',  label: 'Professional & formal',    sub: 'Best for law firms, accountants, medical specialists' },
          { value: 'Friendly & warm',        label: 'Friendly & warm',          sub: 'Best for clinics, service businesses, retail' },
          { value: 'Casual & conversational',label: 'Casual & conversational',  sub: 'Best for fitness, hospitality, youth-facing brands' },
          { value: 'Energetic & enthusiastic',label:'Energetic & enthusiastic', sub: 'Best for sales-driven businesses, real estate' },
        ]} />
      </Field>
      <hr className="sf-divider" />
      <Field label="Opening message when AI answers" required>
        <Textarea id="firstmsg" value={d.firstmsg} onChange={s('firstmsg')}
          placeholder="e.g. Hi! You've reached Smith Dental. I'm Sophie, your virtual assistant. How can I help you today?" minH={68} />
      </Field>
      <div className="sf-row-2">
        <Field label="Brand primary colour" hint="Hex code for your chat widget and portal.">
          <Input id="color" placeholder="#0A0F2C" value={d.color} onChange={s('color')} />
        </Field>
        <Field label="Custom subdomain" hint="yourname.klaivox.com">
          <Input id="domain" placeholder="smith-dental" value={d.domain} onChange={s('domain')} />
        </Field>
      </div>
      <Field label="Logo file (Google Drive or Dropbox link)"
        hint="PNG with transparent background, min 400×400px preferred.">
        <Input id="logo" type="url" placeholder="https://drive.google.com/…" value={d.logo} onChange={s('logo')} />
      </Field>
    </>
  );
}

function Step3({ d, set }) {
  const s = (k) => (v) => set(k, v);
  return (
    <>
      <Field label="Active channels">
        <CheckGrid values={d.channels} onChange={s('channels')} options={[
          { value: 'Voice calls',  label: 'Voice calls',  sub: 'AI answers your phone 24/7' },
          { value: 'Website chat', label: 'Website chat', sub: 'Branded chat widget on your site' },
          { value: 'WhatsApp',     label: 'WhatsApp',     sub: 'WhatsApp Business API' },
          { value: 'SMS',          label: 'SMS',          sub: 'Text message responses' },
          { value: 'Email',        label: 'Email',        sub: 'Gmail / SendGrid' },
        ]} />
      </Field>
      <hr className="sf-divider" />
      <Field label="Appointment booking">
        <RadioGroup name="booking" value={d.booking} onChange={s('booking')} options={[
          { value: 'Google Calendar', label: 'Yes — I use Google Calendar', sub: "We'll connect via OAuth" },
          { value: 'Cal.com',         label: 'Yes — I use Cal.com' },
          { value: 'Other system',    label: 'Yes — I use another system', sub: 'Specify below' },
          { value: 'No — lead capture only', label: 'No — just lead capture for now' },
        ]} />
      </Field>
      <Field label="Calendar system (if other)">
        <Input id="cal_other" placeholder="e.g. Calendly, Acuity, Jane App, Jobber…" value={d.cal_other} onChange={s('cal_other')} />
      </Field>
      <Field label="Google Calendar email (if applicable)"
        hint="We'll request read/write access via Google OAuth on the setup call.">
        <Input id="cal_email" type="email" placeholder="bookings@yourbusiness.com" value={d.cal_email} onChange={s('cal_email')} />
      </Field>
      <div className="sf-row-2">
        <Field label="Business hours for bookings">
          <Input id="hours" placeholder="e.g. Mon–Fri 9am–5pm, Sat 10am–2pm EST" value={d.hours} onChange={s('hours')} />
        </Field>
        <Field label="Average appointment duration">
          <Input id="appt_dur" placeholder="e.g. 30 min, 1 hour" value={d.appt_dur} onChange={s('appt_dur')} />
        </Field>
      </div>
    </>
  );
}

function Step4({ d, set }) {
  const s = (k) => (v) => set(k, v);
  return (
    <>
      <Field label="Your services or products (with pricing if possible)" required>
        <Textarea id="services" value={d.services} onChange={s('services')}
          placeholder={'e.g.\n— Teeth cleaning — $120\n— Invisalign consultation — free\n— Emergency appointment — $80 same-day\n— Whitening — $250'} minH={120} />
      </Field>
      <Field label="Top questions customers ask you most often">
        <Textarea id="faqs" value={d.faqs} onChange={s('faqs')}
          placeholder={'Q: Do you accept insurance?\nA: Yes, most major plans.\n\nQ: How long is a cleaning?\nA: About 45–60 minutes.'} minH={130} />
      </Field>
      <Field label="Things the AI should NEVER say or do">
        <Textarea id="never" value={d.never} onChange={s('never')}
          placeholder="e.g. Never quote exact prices for complex procedures. Never promise same-day availability. Always escalate billing disputes to the manager." minH={80} />
      </Field>
      <Field label="Links to documents for the knowledge base"
        hint="We'll upload these into your AI's knowledge base during setup.">
        <Textarea id="docs" value={d.docs} onChange={s('docs')}
          placeholder="Google Drive / Dropbox links to: pricing guides, service menus, intake forms, FAQs PDFs…" minH={72} />
      </Field>
      <Field label="Languages to support">
        <TagGroup values={d.languages} onChange={s('languages')}
          options={['English','French','Spanish','Arabic','Mandarin','Hindi','Portuguese','Punjabi','Tagalog','Other']} />
      </Field>
      <Field label="Other languages not listed">
        <Input id="lang_other" placeholder="e.g. Ukrainian, Vietnamese, Somali…" value={d.lang_other} onChange={s('lang_other')} />
      </Field>
    </>
  );
}

function Step5({ d, set }) {
  const s = (k) => (v) => set(k, v);
  return (
    <>
      <GroupLabel>CRM / Contact management</GroupLabel>
      <CheckGrid values={d.crm} onChange={s('crm')} options={[
        { value: 'HubSpot',                 label: 'HubSpot' },
        { value: 'Salesforce',              label: 'Salesforce' },
        { value: 'Zoho CRM',               label: 'Zoho CRM' },
        { value: 'Streak',                  label: 'Streak' },
        { value: 'Google Contacts',         label: 'Google Contacts' },
        { value: 'Klaivox CRM (built-in)', label: 'Klaivox CRM', sub: 'Built-in, no setup needed' },
        { value: 'None yet',               label: 'None yet' },
      ]} />

      <GroupLabel>Payments</GroupLabel>
      <CheckGrid values={d.payments} onChange={s('payments')} options={[
        { value: 'Stripe',      label: 'Stripe' },
        { value: 'Square',      label: 'Square' },
        { value: 'PayPal',      label: 'PayPal' },
        { value: 'Not needed',  label: 'Not needed' },
      ]} />

      <GroupLabel>E-commerce</GroupLabel>
      <CheckGrid values={d.ecom} onChange={s('ecom')} options={[
        { value: 'Shopify',        label: 'Shopify' },
        { value: 'WooCommerce',    label: 'WooCommerce' },
        { value: 'Not applicable', label: 'Not applicable' },
      ]} />

      <Field label="Any other software or tools" style={{ marginTop: '1.25rem' }}>
        <Textarea id="other_tools" value={d.other_tools} onChange={s('other_tools')}
          placeholder="e.g. Jane App (clinic), Jobber (field service), custom booking portal, Zendesk for support…" minH={72} />
      </Field>

      <Field label="How should we notify you of new leads?">
        <RadioGroup name="notify" value={d.notify} onChange={s('notify')} options={[
          { value: 'Email notification', label: 'Email me every new lead' },
          { value: 'SMS / text',         label: 'SMS / text message' },
          { value: 'Slack',              label: 'Slack' },
          { value: 'Dashboard only',     label: "Dashboard only — I'll check it myself" },
        ]} />
      </Field>
    </>
  );
}

function Step6({ d, set }) {
  const s = (k) => (v) => set(k, v);
  return (
    <>
      <div className="sf-row-2">
        <Field label="Your full name" required>
          <Input id="name" placeholder="First Last" value={d.name} onChange={s('name')} />
        </Field>
        <Field label="Your role" required>
          <Input id="role" placeholder="Owner, Manager, Director…" value={d.role} onChange={s('role')} />
        </Field>
      </div>
      <div className="sf-row-2">
        <Field label="Email address" required>
          <Input id="email" type="email" placeholder="you@yourbusiness.com" value={d.email} onChange={s('email')} />
        </Field>
        <Field label="Mobile number" required>
          <Input id="mobile" type="tel" placeholder="+1 (416) 000-0000" value={d.mobile} onChange={s('mobile')} />
        </Field>
      </div>
      <Field label="Best times for a setup call">
        <Textarea id="availability" value={d.availability} onChange={s('availability')}
          placeholder="e.g. Any weekday after 2pm EST, or Saturday mornings" minH={64} />
      </Field>
      <Field label="Which plan are you on?">
        <RadioGroup name="plan" value={d.plan} onChange={s('plan')} options={[
          { value: 'Basic — $29 CAD/mo',   label: 'Basic — $29 CAD/mo',   sub: 'Website chat, lead capture, automated email follow-ups' },
          { value: 'Growth — $99 CAD/mo',  label: 'Growth — $99 CAD/mo',  sub: 'Chat + phone agent, appointment booking, 100 voice minutes' },
          { value: 'Advanced — Custom',    label: 'Advanced — Custom pricing', sub: 'Multiple agents, custom integrations, dedicated onboarding' },
          { value: 'Undecided',            label: "Not sure — help me choose on the call" },
        ]} />
      </Field>
      <Field label="How did you hear about Klaivox?">
        <Select id="source" value={d.source} onChange={s('source')}>
          <option value="">Select…</option>
          {['LinkedIn','Google Search','Instagram','YouTube','Referral from someone I know','Cold email / outreach','Other'].map(o => (
            <option key={o}>{o}</option>
          ))}
        </Select>
      </Field>
      <Field label="Anything else we should know before the call?">
        <Textarea id="notes" value={d.notes} onChange={s('notes')}
          placeholder="Special requirements, existing tools to replace, specific goals, tight timeline…" minH={80} />
      </Field>
    </>
  );
}

const STEP_PANELS = [Step1, Step2, Step3, Step4, Step5, Step6];

/* ══════════════════════════════════════════════════
   SUCCESS / SUMMARY
══════════════════════════════════════════════════ */

function SuccessScreen({ d, onRestart }) {
  return (
    <div className="sf-success">
      <div className="sf-success__icon" aria-hidden="true">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
          stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h2 className="sf-success__title">
        {d.name ? `You're all set, ${d.name.split(' ')[0]}!` : "You're all set!"}
      </h2>
      <p className="sf-success__sub">
        Your answers have been saved. Our team will review your submission and reach
        out within a few hours to book your setup call — usually the same or next
        business day.
      </p>
      <div className="sf-success__check-list">
        {['Setup form saved', 'Team notified', 'Onboarding call coming up'].map((item, i) => (
          <div key={i} className="sf-success__check-row">
            <div className="sf-success__check-dot" aria-hidden="true">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            {item}
          </div>
        ))}
      </div>
      <button type="button" className="sf-btn-restart" onClick={onRestart}>
        Submit another response
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════ */
export default function SetupForm() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(INIT);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const setField = useCallback((key, val) => {
    setData(prev => ({ ...prev, [key]: val }));
    setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  }, []);

  const validate = () => {
    const req = REQUIRED[step] || [];
    const errs = {};
    req.forEach(k => {
      if (!data[k] || !String(data[k]).trim()) {
        errs[k] = 'This field is required.';
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goNext = async () => {
    if (!validate()) {
      const firstErr = document.querySelector('.sf-input--err');
      firstErr?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (step === STEP_META.length - 1) {
      setSaving(true);
      setSaveError('');
      try {
        await saveSubmission(data);
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (err) {
        console.error(err);
        setSaveError('Something went wrong saving your form. Please try again.');
      } finally {
        setSaving(false);
      }
    } else {
      setStep(s => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goBack = () => {
    setStep(s => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRestart = () => {
    setStep(0);
    setData(INIT);
    setErrors({});
    setSubmitted(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const StepPanel = STEP_PANELS[step];
  const meta = STEP_META[step];

  if (submitted) {
    return (
      <main className="sf-page">
        <div className="sf-bg-glow sf-bg-glow--1" aria-hidden="true" />
        <div className="sf-bg-glow sf-bg-glow--2" aria-hidden="true" />
        <div className="sf-wrap">
          <div className="sf-card">
            <SuccessScreen d={data} onRestart={handleRestart} />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="sf-page">
      <div className="sf-bg-glow sf-bg-glow--1" aria-hidden="true" />
      <div className="sf-bg-glow sf-bg-glow--2" aria-hidden="true" />

      <div className="sf-wrap">

        {/* ── intro ── */}
        {step === 0 && (
          <div className="sf-intro">
            <div className="sf-intro__chip">Setup questionnaire</div>
            <h1 className="sf-intro__title">
              Let's build your <span>AI agent</span>
            </h1>
            <p className="sf-intro__sub">
              Fill this out before your onboarding call and we'll have your AI
              configured and ready to go live within 24 hours.
            </p>
          </div>
        )}

        {/* ── progress bar ── */}
        <div className="sf-progress" role="progressbar" aria-valuenow={step + 1} aria-valuemax={STEP_META.length}>
          <div className="sf-progress__track">
            {STEP_META.map((m, i) => (
              <div
                key={i}
                className={`sf-progress__seg ${i < step ? 'sf-progress__seg--done' : i === step ? 'sf-progress__seg--active' : ''}`}
              />
            ))}
          </div>
          <div className="sf-progress__labels">
            {STEP_META.map((m, i) => (
              <span key={i} className={`sf-progress__label ${i === step ? 'sf-progress__label--active' : ''}`}>
                {m.short}
              </span>
            ))}
          </div>
        </div>

        {/* ── card ── */}
        <div className="sf-card">
          <p className="sf-step-label">{meta.label}</p>
          <StepPanel d={data} set={setField} errors={errors} />

          {/* nav */}
          {saveError && (
            <p className="sf-save-error">{saveError}</p>
          )}
          <div className="sf-nav">
            <button
              type="button"
              className="sf-btn-back"
              onClick={goBack}
              style={{ visibility: step === 0 ? 'hidden' : 'visible' }}
              aria-label="Previous step"
            >
              <ArrowLeft />
              Back
            </button>
            <span className="sf-nav__count">{step + 1} / {STEP_META.length}</span>
            <button
              type="button"
              className={`sf-btn-next ${step === STEP_META.length - 1 ? 'sf-btn-next--submit' : ''}`}
              onClick={goNext}
              disabled={saving}
            >
              {saving ? 'Saving…' : step === STEP_META.length - 1 ? 'Submit' : 'Continue'}
              {!saving && <ArrowRight />}
            </button>
          </div>
        </div>

      </div>
    </main>
  );
}
